import { blogSlugFromPathname, createLocaleController } from '@saas-pages/shared/public/i18n'
import { getPostBySlug } from '@/shared/blog/posts'

export const locales = ['ru', 'en'] as const
export type Locale = (typeof locales)[number]

const ru = {
  nav: { all: 'Все инструменты', voice: 'Диктофон', circle: 'Круглое видео', blog: 'Блог' },
  common: {
    local: 'Локально в браузере', noUpload: 'Записи не загружаются на сервер',
    retry: 'Попробовать снова', start: 'Начать запись', stop: 'Остановить', pause: 'Пауза', resume: 'Продолжить',
    reset: 'Новая запись', download: 'Скачать', ready: 'Устройство готово', requesting: 'Ждём разрешения браузера…',
    format: 'Формат', finished: 'Запись готова', timer: 'Время записи', preview: 'Предпросмотр записи', recording: 'Идёт запись', paused: 'Запись на паузе',
  },
  voice: {
    title: 'Диктофон онлайн', description: 'Запишите голос с микрофона, прослушайте результат и скачайте файл. Всё остаётся на вашем устройстве.', permission: 'Для записи нужен доступ к микрофону.', limit: 'До 30 минут', useCases: ['Микрофон', 'Пауза', 'Скачивание'], allow: 'Включить микрофон',
    errors: {
      unsupported: 'Этот браузер не умеет записывать звук. Откройте страницу в свежем Chrome, Edge, Firefox или Safari.',
      permissionDenied: 'Браузер запретил доступ к микрофону. Разрешите его в настройках сайта (значок слева от адреса) и попробуйте снова.',
      deviceMissing: 'Микрофон не найден. Подключите его и попробуйте снова.',
      busy: 'Микрофон занят другим приложением или вкладкой. Закройте их и попробуйте снова.',
      genericError: 'Запись не удалась. Попробуйте ещё раз или перезагрузите страницу.',
    },
    processing: { label: 'Локально в браузере', title: 'Аудиозапись остаётся на устройстве и не отправляется на сервер' },
    howTo: ['Разрешите браузеру доступ к микрофону.', 'Начните запись; при необходимости поставьте её на паузу.', 'Остановите, прослушайте и скачайте готовый файл.'],
    faq: [
      { question: 'Запись отправляется на сервер?', answer: 'Нет. Запись создаётся локально средствами браузера и остаётся на вашем устройстве.' },
      { question: 'В каком формате сохранится голос?', answer: 'Формат зависит от браузера. Инструмент выбирает только реально поддерживаемый вариант и показывает его перед записью.' },
      { question: 'Почему браузер просит доступ к микрофону?', answer: 'Без разрешения сайт не может получить звуковой поток. Доступ используется только в открытой вкладке для текущей записи.' },
    ],
    related: { label: 'Круглое видео', description: 'Запись с камеры и микрофона с круглым предпросмотром.' },
  },
  audioTrim: {
    title: 'Обрезать аудио онлайн',
    description: 'Обрежьте один локальный аудиофайл, сохранив исходный кодек и контейнер без перекодирования.',
  },
  circle: {
    title: 'Запись круглого видео', description: 'Запишите видео с камеры в круглой рамке и скачайте совместимый квадратный видеофайл.', permission: 'Для записи нужны камера и микрофон.', allow: 'Включить камеру', mirror: 'Отразить изображение', limit: 'До 60 секунд', honest: 'Скачивается квадратное видео. Это не нативная «заметка-кружок» мессенджера: её формат зависит от конкретного приложения.', useCases: ['Камера', 'Квадратный файл', 'До 60 секунд'],
    errors: {
      unsupported: 'Этот браузер не умеет записывать видео. Откройте страницу в свежем Chrome, Edge, Firefox или Safari.',
      permissionDenied: 'Браузер запретил доступ к камере или микрофону. Разрешите их в настройках сайта (значок слева от адреса) и попробуйте снова.',
      deviceMissing: 'Камера или микрофон не найдены. Подключите их и попробуйте снова.',
      busy: 'Камера или микрофон заняты другим приложением или вкладкой. Закройте их и попробуйте снова.',
      genericError: 'Запись не удалась. Попробуйте ещё раз или перезагрузите страницу.',
    },
    processing: { label: 'Локально в браузере', title: 'Камера и микрофон обрабатываются в браузере без загрузки видео на сервер' },
    howTo: ['Разрешите доступ к камере и микрофону.', 'Настройте зеркальное отображение и начните запись.', 'Остановите запись или дождитесь лимита 60 секунд, затем скачайте квадратное видео.'],
    faq: [
      { question: 'Получится нативный видеокружок для мессенджера?', answer: 'Нет. Сайт создаёт обычный квадратный видеофайл. Конкретный мессенджер сам решает, как импортировать и показывать такой файл.' },
      { question: 'Видео загружается на сервер?', answer: 'Нет. Поток камеры, кадрирование, предпросмотр и готовый файл остаются в браузере.' },
      { question: 'Почему запись ограничена 60 секундами?', answer: 'Это безопасный предел по умолчанию для короткого видео и памяти вкладки. Запись автоматически остановится на отметке 60 секунд.' },
    ],
    related: { label: 'Диктофон', description: 'Быстрая запись только с микрофона.' },
  },
  home: { title: 'Запись и обрезка медиа онлайн', description: 'Диктофон, обрезка аудио и круглое видео прямо в браузере, без загрузки на сервер.' },
}

export type Translations = typeof ru

const en: Translations = {
  nav: { all: 'All tools', voice: 'Voice recorder', circle: 'Circular video', blog: 'Blog' },
  common: {
    local: 'Local in your browser', noUpload: 'Recordings are never uploaded',
    retry: 'Try again', start: 'Start recording', stop: 'Stop', pause: 'Pause', resume: 'Resume',
    reset: 'New recording', download: 'Download', ready: 'Device is ready', requesting: 'Waiting for browser permission…',
    format: 'Format', finished: 'Recording ready', timer: 'Recording time', preview: 'Recording preview', recording: 'Recording', paused: 'Recording paused',
  },
  voice: {
    title: 'Online voice recorder', description: 'Record your microphone, listen to the result, and download the file. Everything stays on your device.', permission: 'Microphone access is required to record.', limit: 'Up to 30 minutes', useCases: ['Microphone', 'Pause', 'Download'], allow: 'Turn on microphone',
    errors: {
      unsupported: 'This browser cannot record audio. Open the page in a recent Chrome, Edge, Firefox or Safari.',
      permissionDenied: 'The browser blocked microphone access. Allow it in site settings (the icon next to the address) and try again.',
      deviceMissing: 'No microphone was found. Connect one and try again.',
      busy: 'The microphone is busy in another app or tab. Close it and try again.',
      genericError: 'Recording failed. Try again or reload the page.',
    },
    processing: { label: 'Local in your browser', title: 'Audio stays on your device and is never sent to a server' },
    howTo: ['Allow microphone access in your browser.', 'Start recording and pause it when needed.', 'Stop, listen to, and download the finished file.'],
    faq: [
      { question: 'Is the recording sent to a server?', answer: 'No. The browser creates the recording locally and keeps it on your device.' },
      { question: 'Which audio format will be used?', answer: 'The format depends on your browser. The tool selects an actually supported option and shows it before recording.' },
      { question: 'Why does the browser request microphone access?', answer: 'The site cannot receive audio without permission. Access is used only in the open tab for the current recording.' },
    ],
    related: { label: 'Circular video', description: 'Record your camera and microphone with a circular preview.' },
  },
  audioTrim: {
    title: 'Trim audio online',
    description: 'Trim one local audio file while preserving its original codec and container without transcoding.',
  },
  circle: {
    title: 'Circular video recorder', description: 'Record camera video in a circular preview and download a compatible square video file.', permission: 'Camera and microphone access are required.', allow: 'Turn on camera', mirror: 'Mirror the image', limit: 'Up to 60 seconds', honest: 'The download is a square video. It is not a native messenger video note, whose format depends on the specific app.', useCases: ['Camera', 'Square file', 'Up to 60 seconds'],
    errors: {
      unsupported: 'This browser cannot record video. Open the page in a recent Chrome, Edge, Firefox or Safari.',
      permissionDenied: 'The browser blocked camera or microphone access. Allow them in site settings (the icon next to the address) and try again.',
      deviceMissing: 'No camera or microphone was found. Connect them and try again.',
      busy: 'The camera or microphone is busy in another app or tab. Close it and try again.',
      genericError: 'Recording failed. Try again or reload the page.',
    },
    processing: { label: 'Local in your browser', title: 'Camera and microphone data stays in the browser and is never uploaded' },
    howTo: ['Allow camera and microphone access.', 'Choose the mirror setting and start recording.', 'Stop or wait for the 60-second limit, then download the square video.'],
    faq: [
      { question: 'Will this create a native messenger video note?', answer: 'No. The tool creates a regular square video file. Each messenger decides how to import and display it.' },
      { question: 'Is the video uploaded?', answer: 'No. The camera stream, crop, preview, and finished file remain in your browser.' },
      { question: 'Why is recording limited to 60 seconds?', answer: 'This is a safe default for short video and tab memory. Recording stops automatically at 60 active seconds.' },
    ],
    related: { label: 'Voice recorder', description: 'Quick microphone-only recording.' },
  },
  home: { title: 'Record and trim media online', description: 'Voice recorder, audio trimmer and circular video right in your browser, with no uploads.' },
}

export function resolvePathLocale(pathname: string): Locale | null {
  const slug = blogSlugFromPathname(pathname)
  return slug ? getPostBySlug(slug)?.locale ?? null : null
}

const controller = createLocaleController({
  locales,
  defaultLocale: 'ru',
  storageKey: 'mediatik.locale',
  translations: { ru, en },
  resolvePathLocale,
})

export const { I18nProvider, useLocale, useT } = controller
